import type { HubspotCityOption } from "@/constants/hubspot-ticket-cities";

/** Drops accents and case so "Sao Paulo" and "São Paulo" compare equal. */
export function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/** The UF at the end of a Woba address ("Rua A, 100 - Pinheiros - São Paulo, SP"). */
export function extractUf(address: string | undefined | null): string | null {
  if (!address) return null;
  const match = /(?:^|[,\s-])([A-Za-z]{2})\s*$/.exec(address.trim());
  return match ? match[1].toUpperCase() : null;
}

/**
 * Matches a room's free-text city against the HubSpot dropdown, so the ticket
 * carries a value the Zap accepts (PED-13885 item 2).
 *
 * The UF is what disambiguates the same city name in two states, so an
 * address that carries one is preferred; without it, a single city match
 * still wins, and an ambiguous one resolves to nothing rather than guessing.
 * Returns the option `value` (what travels), never the label.
 */
export function resolveHubspotCity(
  city: string | undefined | null,
  address: string | undefined | null,
  options: HubspotCityOption[],
): string | null {
  const wanted = normalize(city ?? "");
  if (!wanted) return null;

  const uf = extractUf(address);
  // An option is "Cidade - UF"; "Lorena-SP" and "Outras" are the exceptions.
  const candidates = options.filter((option) => {
    const [name] = option.label.split(/\s*-\s*/);
    return normalize(name ?? "") === wanted;
  });
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0].value;

  if (uf) {
    const byUf = candidates.find((option) =>
      option.label.toUpperCase().endsWith(`- ${uf}`),
    );
    if (byUf) return byUf.value;
  }
  // Two same-named cities and nothing to tell them apart — sending the wrong
  // state is worse than sending none, and the select lets ops-facing input
  // pick it explicitly.
  return null;
}

/**
 * Exact match of a "Cidade - UF" string against the dropdown, ignoring accents,
 * case and the spacing around the dash ("Lorena-SP"). Unlike
 * `resolveHubspotCity`, the UF must match too: a prefill for "Campinas - GO"
 * leaves the field blank instead of picking "Campinas - SP" (PED-14049).
 * Returns the option `value`, never the label.
 */
export function matchHubspotCityOption(
  cityUf: string | undefined | null,
  options: HubspotCityOption[],
): string | null {
  const key = (value: string) => normalize(value).replace(/\s*-\s*/g, " - ");
  const wanted = key(cityUf ?? "");
  if (!wanted) return null;
  const match = options.find(
    (option) => key(option.label) === wanted || key(option.value) === wanted,
  );
  return match ? match.value : null;
}
